import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, BookOpen, ArrowRight, Loader as Loader2, Map, Brain, Rocket, ArrowLeft, CircleCheck as CheckCircle, BookMarked, GraduationCap, Code, Briefcase, Hash, Clock, Target, Menu, X, CreditCard as Edit, Check, LogOut, Plus, Book, Copy, ArrowUpRight, LogIn } from 'lucide-react';
import { TypeAnimation } from 'react-type-animation';
import ReactMarkdown from 'react-markdown';
import { RoadmapFlow } from './components/RoadmapFlow';
import { firebaseService } from './services/firebaseService';
import { TextFormatToolbar } from './components/TextFormatToolbar';
import { LearningPreferencesForm } from './components/LearningPreferencesForm';
import { AuthModal } from './components/AuthModal';
import { DashboardView } from './components/DashboardView';
import { GeneratingPopup } from './components/GeneratingPopup';
import { LessonGeneratingPopup } from './components/LessonGeneratingPopup';
import { ResourceLinks } from './components/ResourceLinks';
import { QuizModal } from './components/QuizModal';
import { CareerTransitionForm } from './components/CareerTransitionForm';
import { guestStorageService } from './services/guestStorage';
import { auth, isInitialized } from './services/firebase';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';

interface Module {
  title: string;
  lessons: string[];
}

interface RoadmapData {
  modules: Module[];
  loading: boolean;
  error: string | null;
}

interface ResourceLink {
  title: string;
  url: string;
  description: string;
}

interface LessonContent {
  title: string;
  moduleTitle: string;
  content: string | null;
  loading: boolean;
  resourceLinks?: ResourceLink[];
}

interface CompletedLessons {
  [key: string]: boolean;
}

interface LearningPreferences {
  level: string;
  goal: string;
  timeCommitment: string;
}

function ModuleCard({ 
  title, 
  lessons, 
  moduleIndex, 
  onSelectLesson,
  completedLessons,
  onToggleComplete,
  selectedLesson
}: { 
  title: string; 
  lessons: string[]; 
  moduleIndex: number;
  onSelectLesson: (lesson: string, moduleTitle: string) => void;
  completedLessons: CompletedLessons;
  onToggleComplete: (lesson: string) => void;
  selectedLesson: LessonContent | null;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-lg">
      <div 
        className="p-4 cursor-pointer hover:bg-blue-50/50 transition-colors duration-200"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-blue-100 rounded-lg p-1.5">
              <BookOpen className="w-4 h-4 text-blue-600" />
            </div>
            <h3 className="text-sm font-medium text-gray-800">Module {moduleIndex + 1}: {title}</h3>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-1">
              {lessons.map((lesson, index) => {
                const isSelected = selectedLesson?.title === lesson;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-2"
                  >
                    <button
                      onClick={() => onSelectLesson(lesson, title)}
                      className={`flex-1 flex items-center gap-2 p-2 rounded-lg group transition-colors duration-200 text-left ${
                        isSelected 
                          ? 'bg-blue-50 text-blue-700' 
                          : 'hover:bg-blue-50/50 text-gray-700'
                      }`}
                    >
                      <span className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full text-xs font-medium ${
                        isSelected 
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-blue-50 text-blue-600'
                      }`}>
                        {index + 1}
                      </span>
                      <span className="text-sm flex-1">{lesson}</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const hasContent = firebaseService.getLessonFromCache(lesson);
                        if (!hasContent) {
                          return;
                        }
                        onToggleComplete(lesson);
                      }}
                      className={`p-1.5 rounded-lg transition-colors duration-200 ${!firebaseService.getLessonContent(lesson) ? 'opacity-50 cursor-not-allowed' : ''} ${
                        completedLessons[lesson] 
                          ? 'text-green-600 hover:bg-green-50' 
                          : 'text-gray-400 hover:bg-gray-50'
                      }`}
                      title={firebaseService.getLessonContent(lesson) ? 'Mark lesson as complete' : 'View lesson content first'}
                    >
                      <CheckCircle className="w-5 h-5" />
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { courseId } = useParams();
  const [subject, setSubject] = useState('');
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<LearningPreferences>({
    level: 'Beginner',
    goal: '',
    timeCommitment: '2-5 hours'
  });
  const [roadmapData, setRoadmapData] = useState<RoadmapData>({
    modules: [],
    loading: false,
    error: null
  });
  const [showRoadmap, setShowRoadmap] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<LessonContent | null>(null);
  const [completedLessons, setCompletedLessons] = useState<CompletedLessons>({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [roadmapSlug, setRoadmapSlug] = useState<string | null>(null);
  const [userRoadmaps, setUserRoadmaps] = useState<any[]>([]);
  const [showDashboard, setShowDashboard] = useState(false);
  const [activeTab, setActiveTab] = useState<'new' | 'courses'>('new');
  const [showGeneratingPopup, setShowGeneratingPopup] = useState(false);
  const [showLessonGenerating, setShowLessonGenerating] = useState(false);
  const [isSharedView, setIsSharedView] = useState(false);
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [showCareerTransition, setShowCareerTransition] = useState(false);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let unsubscribe = () => {};

    if (isInitialized && auth) {
      unsubscribe = onAuthStateChanged(auth, async (user) => {
        try {
          setUser(user);
          if (user) {
            setIsGuestMode(false);
            const progress = await firebaseService.getAllUserRoadmaps(user.uid);
            setUserRoadmaps(progress);
            setShowDashboard(progress.length > 0);
          } else {
            if (!isGuestMode) {
              setShowDashboard(false);
              setUserRoadmaps([]);
            }
          }
        } catch (error) {
          console.error('Error handling auth state change:', error);
          setUser(null);
          if (!isGuestMode) {
            setShowDashboard(false);
            setUserRoadmaps([]);
          }
        }
      });
    } else {
      console.warn('Firebase authentication is not initialized');
      setUser(null);
      if (!isGuestMode) {
        setShowDashboard(false);
        setUserRoadmaps([]);
      }
    }

    return () => unsubscribe();
  }, [isGuestMode]);

  useEffect(() => {
    const loadCourse = async () => {
      if (courseId && (user || isGuestMode)) {
        try {
          const roadmaps = isGuestMode 
            ? guestStorageService.getAllRoadmaps()
            : await firebaseService.getAllUserRoadmaps(user!.uid);
          const roadmap = roadmaps.find((r: any) => r.slug === courseId);
          
          if (roadmap) {
            setSubject(roadmap.subject);
            setRoadmapData({
              modules: roadmap.modules,
              loading: false,
              error: null
            });
            setPreferences(roadmap.preferences);
            setCompletedLessons(roadmap.completedLessons || {});
            setRoadmapSlug(roadmap.slug);
            setShowDashboard(false);
          } else {
            navigate('/', { replace: true });
          }
        } catch (error) {
          console.error('Error loading course:', error);
          navigate('/', { replace: true });
        }
      }
    };

    loadCourse();
  }, [courseId, user, isGuestMode, navigate]);

  useEffect(() => {
    const loadSharedRoadmap = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sharedSlug = urlParams.get('share');
      
      if (sharedSlug) {
        try {
          const sharedRoadmap = await firebaseService.getSharedRoadmap(sharedSlug);
          if (sharedRoadmap) {
            setSubject(sharedRoadmap.subject);
            setRoadmapData({
              modules: sharedRoadmap.modules,
              loading: false,
              error: null
            });
            setPreferences(sharedRoadmap.preferences);
            setRoadmapSlug(sharedSlug);
            setIsSharedView(true);
          }
        } catch (error) {
          console.error('Error loading shared roadmap:', error);
        }
      }
    };

    loadSharedRoadmap();
  }, []);

  const handleCloneRoadmap = async () => {
    if (!user && !isGuestMode) {
      setShowAuthModal(true);
      return;
    }

    if (!roadmapSlug) return;

    try {
      const newSlug = isGuestMode
        ? await cloneSharedRoadmapForGuest(roadmapSlug)
        : await firebaseService.cloneSharedRoadmap(user!.uid, roadmapSlug);
      const progress = isGuestMode
        ? guestStorageService.getAllRoadmaps()
        : await firebaseService.getAllUserRoadmaps(user!.uid);
      setUserRoadmaps(progress);
      setRoadmapSlug(newSlug);
      setIsSharedView(false);
    } catch (error) {
      console.error('Error cloning roadmap:', error);
    }
  };

  const cloneSharedRoadmapForGuest = async (slug: string) => {
    const sharedRoadmap = await firebaseService.getSharedRoadmap(slug);
    if (!sharedRoadmap) {
      throw new Error('Shared roadmap not found');
    }
    return guestStorageService.saveRoadmap(sharedRoadmap.subject, sharedRoadmap.modules, sharedRoadmap.preferences);
  };

  const handleGuestMode = () => {
    setIsGuestMode(true);
    setUser(null);
    const guestRoadmaps = guestStorageService.getAllRoadmaps();
    setUserRoadmaps(guestRoadmaps);
    setShowDashboard(guestRoadmaps.length > 0);
    setShowAuthModal(false);
  };

  const handleLogout = async () => {
    try {
      if (user) {
        await signOut(auth);
      }
      if (isGuestMode) {
        guestStorageService.clearAllData();
      }
      setIsGuestMode(false);
      resetToHome();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleGenerateRoadmap = async () => {
    if (!subject.trim() || !preferences.goal.trim()) return;
    
    if (!user && !isGuestMode) {
      setShowAuthModal(true);
      return;
    }

    console.log('Starting roadmap generation:', {
      subject,
      preferences,
      user: user?.uid || 'guest'
    });

    setRoadmapData({ ...roadmapData, loading: true, error: null });
    setShowRoadmap(false);
    setSelectedLesson(null);

    setShowGeneratingPopup(true);

    try {
      const response = await firebaseService.generateRoadmap(
        subject,
        preferences.level,
        preferences.goal,
        preferences.timeCommitment
      );

      console.log('Roadmap generation response:', response);

      const slug = isGuestMode
        ? guestStorageService.saveRoadmap(subject, response.modules, preferences)
        : await firebaseService.saveRoadmap(user!.uid, subject, response.modules, preferences);

      const newRoadmap = {
        subject,
        slug,
        modules: response.modules,
        preferences,
        completedLessons: {},
        createdAt: new Date().toISOString()
      };
      setUserRoadmaps(prev => [...prev, newRoadmap]);
      setRoadmapData({
        modules: response.modules,
        loading: false,
        error: null
      });
      setRoadmapSlug(slug);
      
      setShowDashboard(false);
      setActiveTab('new');
      setShowGeneratingPopup(false);
    } catch (error) {
      console.error('Roadmap generation error:', error);
      setRoadmapData({
        ...roadmapData,
        loading: false,
        error: `Failed to generate roadmap: ${error.message}. Please check the browser console for more details.`
      });
      setShowGeneratingPopup(false);
    }
  };

  const generateLessonContent = async (lesson: string, moduleTitle: string) => {
    if (!user && !isGuestMode) return;
  
    // Try to get content from Firebase first
    const savedContent = isGuestMode
      ? guestStorageService.getLessonContent(roadmapSlug!, lesson)
      : await firebaseService.getLessonContent(user!.uid, roadmapSlug!, lesson);
    if (savedContent) {
      setSelectedLesson({
        title: lesson,
        moduleTitle: savedContent.moduleTitle,
        content: savedContent.content,
        loading: false,
        resourceLinks: savedContent.resourceLinks
      });
      return;
    }
  
    // Fall back to cache if Firebase content not found
    const cachedContent = firebaseService.getLessonFromCache(lesson);
    if (cachedContent) {
      setSelectedLesson({
        title: lesson,
        moduleTitle: cachedContent.moduleTitle,
        content: cachedContent.content,
        loading: false
      });
      return;
    }
  
    // Initialize lesson with loading state
    setSelectedLesson({
      title: lesson,
      moduleTitle,
      content: null,
      loading: true
    });
  
    try {
      // Generate lesson content with streaming
      const response = await firebaseService.generateLessonContent(
        lesson,
        moduleTitle,
        preferences.level,
        preferences.timeCommitment,
        preferences.goal
      );

      // Set up streaming content
      setSelectedLesson(prev => ({
        ...prev!,
        content: '',
        loading: false
      }));

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      if (reader) {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);
                if (data === '[DONE]') {
                  break;
                }
                
                try {
                  const parsed = JSON.parse(data);
                  const content = parsed.choices?.[0]?.delta?.content;
                  if (content) {
                    fullContent += content;
                    setSelectedLesson(prev => ({
                      ...prev!,
                      content: fullContent
                    }));
                  }
                } catch (e) {
                  // Skip invalid JSON lines
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      }

      // Generate resource links after content is complete
      const resourceLinks = await firebaseService.generateResourceLinks(lesson, moduleTitle);
      
      const lessonContent = {
        content: fullContent,
        moduleTitle,
        resourceLinks
      };

      // Save to Firebase
      if (isGuestMode) {
        guestStorageService.saveLessonContent(roadmapSlug!, lesson, lessonContent);
      } else {
        await firebaseService.saveLessonContent(user!.uid, roadmapSlug!, lesson, lessonContent);
      }

      // Update with resource links
      setSelectedLesson(prev => ({
        ...prev!,
        resourceLinks
      }));

    } catch (error) {
      console.error('Error generating lesson content:', error);
      setSelectedLesson(prev => ({
        ...prev!,
        content: "Failed to generate lesson content. Please try again.",
        loading: false
      }));
    }
  };

  const handleStartQuiz = async () => {
    if (!selectedLesson?.content) return;
    
    setLoadingQuiz(true);
    setShowQuiz(true);
    
    try {
      const questions = await firebaseService.generateQuizQuestions(
        selectedLesson.title,
        selectedLesson.moduleTitle,
        selectedLesson.content
      );
      setQuizQuestions(questions);
    } catch (error) {
      console.error('Error generating quiz:', error);
    } finally {
      setLoadingQuiz(false);
    }
  };

  const resetToHome = () => {
    navigate('/', { replace: true });
    setRoadmapData({
      modules: [],
      loading: false,
      error: null
    });
    setSelectedLesson(null);
    setShowRoadmap(false);
    setSubject('');
    setCompletedLessons({});
    setShowPreferences(false);
    setPreferences({
      level: 'Beginner',
      goal: '',
      timeCommitment: '2-5 hours'
    });
    setRoadmapSlug(null);
    setShowDashboard(false);
    setActiveTab('new');
  };

  const toggleLessonComplete = async (lesson: string) => {
    if (!user && !isGuestMode) {
      setShowAuthModal(true);
      return;
    }

    // Check if lesson content has been generated
    const lessonContent = firebaseService.getLessonFromCache(lesson);
    if (!lessonContent) {
      return;
    }

    if (!roadmapSlug) {
      console.error('No active roadmap slug found');
      return;
    }

    const newValue = !completedLessons[lesson];
    try {
      if (isGuestMode) {
        guestStorageService.updateLessonCompletion(roadmapSlug, lesson, newValue);
      } else {
        await firebaseService.updateLessonCompletion(user!.uid, roadmapSlug, lesson, newValue);
      }
      setCompletedLessons(prev => ({
        ...prev,
        [lesson]: newValue
      }));
    } catch (error) {
      console.error('Error updating lesson completion:', error);
    }
  };

  const calculateCompletionRate = () => {
    const totalLessons = roadmapData.modules.reduce((acc, module) => acc + module.lessons.length, 0);
    const completedCount = Object.values(completedLessons).filter(Boolean).length;
    return totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;
  };

  const getTotalLessons = () => {
    return roadmapData.modules.reduce((acc, module) => acc + module.lessons.length, 0);
  };

  const handleStartNewRoadmap = () => {
    navigate('/');
    setShowDashboard(false);
    resetToHome();
  };

  const handleContinueRoadmap = async (roadmap: any) => {
    navigate(`/course/${roadmap.slug}`);
    setSubject(roadmap.subject);
    setRoadmapData({
      modules: roadmap.modules,
      loading: false,
      error: null
    });
    setPreferences(roadmap.preferences);
    setCompletedLessons(roadmap.completedLessons || {});
    setRoadmapSlug(roadmap.slug);
    setShowDashboard(false);
  };

  const handleRoadmapDeleted = (slug: string) => {
    setUserRoadmaps(prev => prev.filter(r => r.slug !== slug));
    if (isGuestMode) {
      guestStorageService.deleteRoadmap(slug);
    }
    if (roadmapSlug === slug) {
      resetToHome();
    }
  };

  const handleNavClick = (tab: 'new' | 'courses') => {
    if (!user && !isGuestMode) {
      setShowAuthModal(true);
      return;
    }
    
    setActiveTab(tab);
    setShowCareerTransition(false);
    if (tab === 'new') {
      navigate('/', { replace: true });
      setShowDashboard(false);
      resetToHome();
    } else {
      navigate('/dashboard', { replace: true });
      setShowDashboard(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-blue-50">
      <GeneratingPopup isOpen={showGeneratingPopup} />
      <LessonGeneratingPopup 
        isOpen={showLessonGenerating} 
        lessonTitle={selectedLesson?.title || ''} 
        onCancel={() => {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          setShowLessonGenerating(false);
        }}
      />
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onGuestMode={handleGuestMode}
        onSuccess={() => {
          setShowAuthModal(false);
          if (activeTab === 'new') {
            handleStartNewRoadmap();
          } else {
            setShowDashboard(true);
          }
        }}
      />
      
      <div className="lg:hidden bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            {isSidebarOpen ? (
              <X className="w-6 h-6 text-gray-600" />
            ) : (
              <Menu className="w-6 h-6 text-gray-600" />
            )}
          </button>
          <h1 className="text-lg font-bold text-gray-900">DIY Learn It</h1>
          <div className="w-10" />
        </div>
      </div>

      <div className="flex min-h-[calc(100vh-64px)] lg:min-h-screen">
        <div 
          className={`${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } transform lg:translate-x-0 transition-all duration-300 fixed lg:sticky lg:top-0 z-30 w-72 h-[calc(100vh-64px)] lg:h-screen bg-white/80 backdrop-blur-md border-r border-gray-200/50 lg:flex lg:flex-col shadow-xl shadow-blue-900/5`}
        >
          <div className="p-6 border-b border-gray-200/50 hidden lg:block">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-gradient-to-br from-blue-600 to-blue-500 rounded-xl p-2 shadow-lg shadow-blue-600/10">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
                DIY Learn It
              </h1>
            </div>
            <p className="text-sm text-gray-500">Your personalized learning companion</p>
          </div>
          
          <nav className="flex-1 p-4">
            <div className="space-y-1.5">
              <button
                onClick={() => handleNavClick('new')}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors duration-200 ${
                  activeTab === 'new'
                    ? 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-50/50 hover:text-gray-900'
                }`}
              >
                <Plus className={`w-5 h-5 transition-transform duration-200 ${activeTab === 'new' ? 'scale-110' : ''}`} />
                <span className="text-sm sm:text-base whitespace-nowrap">New Course</span>
              </button>
              
              <button
                onClick={() => handleNavClick('courses')}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors duration-200 ${
                  activeTab === 'courses'
                    ? 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-50/50 hover:text-gray-900'
                }`}
              >
                <Book className={`w-5 h-5 transition-transform duration-200 ${activeTab === 'courses' ? 'scale-110' : ''}`} />
                <span className="text-sm sm:text-base whitespace-nowrap">My Courses</span>
              </button>
              
              {!user && !isGuestMode && (
                <>
                  <button
                    onClick={handleGuestMode}
                    className="w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors duration-200 text-gray-600 hover:bg-green-50/50 hover:text-green-700 border border-green-200/50 hover:border-green-300"
                  >
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-green-600">G</span>
                    </div>
                    <span className="text-sm sm:text-base whitespace-nowrap">Try Guest Mode</span>
                  </button>

                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors duration-200 text-gray-600 hover:bg-blue-50/50 hover:text-blue-700 border border-blue-200/50 hover:border-blue-300"
                  >
                    <LogIn className="w-5 h-5" />
                    <span className="text-sm sm:text-base whitespace-nowrap">Sign In</span>
                  </button>
                </>
              )}
            </div>
          </nav>

          {(user || isGuestMode) && (
            <div className="p-4 border-t border-gray-200/50">
              {user ? (
                <div className="px-4 py-3 mb-3 rounded-lg bg-gray-50/50">
                  <p className="text-sm font-medium text-gray-900">{user.email}</p>
                  <p className="text-xs text-gray-500">Signed in with Google</p>
                </div>
              ) : (
                <div className="px-4 py-3 mb-3 rounded-lg bg-blue-50/50">
                  <p className="text-sm font-medium text-blue-900">Guest Mode</p>
                  <p className="text-xs text-blue-600">Data stored locally</p>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-red-50/50 hover:text-red-600 rounded-lg transition-all duration-200 group"
              >
                <LogOut className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                <span>{user ? 'Sign Out' : 'Exit Guest Mode'}</span>
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {showCareerTransition ? (
            <CareerTransitionForm
              onRoadmapGenerated={(subject, goal) => {
                setSubject(subject);
                setPreferences(prev => ({ ...prev, goal }));
                setShowCareerTransition(false);
                setShowPreferences(true);
                navigate('/');
              }}
            />
          ) : showDashboard && userRoadmaps.length > 0 ? (
            <DashboardView
              roadmaps={userRoadmaps}
              isGuestMode={isGuestMode}
              onStartNewRoadmap={handleStartNewRoadmap}
              onContinueRoadmap={handleContinueRoadmap}
              onRoadmapDeleted={handleRoadmapDeleted}
            />
          ) : !roadmapData.modules.length ? (
            <div className="min-h-screen p-4 md:p-8 lg:p-12">
              <div className="max-w-3xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center mb-8"
                >
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full blur-[100px] opacity-25 animate-pulse"></div>
                      <div className="relative bg-gradient-to-br from-blue-600 to-blue-500 p-3 rounded-2xl transform hover:scale-105 transition-transform duration-300">
                        <Brain className="w-12 h-12 md:w-16 md:h-16 text-white" />
                      </div>
                    </div>
                  </div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500 mb-3">
                    Your Learning Journey Starts Here
                  </h1>
                  <p className="text-base md:text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                    Generate personalized learning roadmaps to master any topic
                  </p>
                  
                  <div className="flex flex-wrap gap-4 justify-center items-center text-sm font-medium mb-6">
                    <div className="flex items-center gap-3 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
                      <Target className="w-5 h-5 text-blue-600" />
                      <span>Goal-Oriented</span>
                    </div>
                    
                    <div className="flex items-center gap-3 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
                      <Clock className="w-5 h-5 text-blue-600"  />
                      <span>Time-Flexible</span>
                    </div>
                    <div className="flex items-center gap-3 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                      <span>Personalized Path</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-blue-400/10 to-blue-300/10 rounded-2xl blur-xl opacity-80 group-hover:opacity-100 transition-opacity duration-500"></div>

                  <div className="relative bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl shadow-blue-500/10">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/50 via-white/30 to-transparent rounded-2xl"></div>
                    
                    <div className="relative p-6 md:p-8">
                      {!showPreferences ? (
                        <div>
                          <div className="relative">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                              <span className="text-lg font-semibold text-gray-800 whitespace-nowrap">What do you want to learn?</span>
                              <TypeAnimation
                                sequence={[
                                  'Web Development',
                                  1500,
                                  'Data Science',
                                  1500,
                                  'Machine Learning',
                                  1500,
                                  'UI/UX Design',
                                  1500,
                                  'Cloud Computing',
                                  1500,
                                  'Artificial Intelligence',
                                  1500,
                                  'Mobile App Development',
                                  1500,
                                  'Blockchain Technology',
                                  1500,
                                  'DevOps Engineering',
                                  1500,
                                  'Cybersecurity',
                                  1500,
                                  'Game Development',
                                  1500,
                                  'Digital Marketing',
                                  1500,
                                ]}
                                wrapper="span"
                                speed={50}
                                className="text-lg font-semibold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500 whitespace-nowrap"
                                repeat={Infinity}
                              />
                            </div>
                            <input
                              type="text"
                              value={subject}
                              onChange={(e) => setSubject(e.target.value)}
                              placeholder="What topic would you like to master?"
                              className="w-full px-4 py-3 text-base bg-white/20 backdrop-blur-md border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400/50 placeholder-gray-500/70 text-gray-800"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && subject.trim()) {
                                  if (!user) {
                                    setShowAuthModal(true);
                                  } else {
                                    setShowPreferences(true);
                                  }
                                }
                              }}
                            />
                          </div>
                          <button
                            onClick={() => {
                              if (!user && !isGuestMode) {
                                setShowAuthModal(true);
                              } else if (subject.trim()) {
                                setShowPreferences(true);
                              }
                            }}
                            disabled={!subject.trim()}
                            className="w-full mt-4 px-6 py-3 text-base font-medium bg-gradient-to-r from-blue-600/90 to-blue-500/90 hover:from-blue-600 hover:to-blue-500 text-white rounded-lg disabled:opacity-50 transition-all duration-300 backdrop-blur-sm flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 hover:scale-[1.02] transform"
                          >
                            Continue
                            <ArrowRight className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center gap-2 mb-6">
                            <button
                              onClick={() => setShowPreferences(false)}
                              className="text-gray-600 hover:text-blue-600 transition-colors"
                            >
                              <ArrowLeft className="w-5 h-5" />
                            </button>
                            <h2 className="text-xl font-semibold text-gray-800">
                              Tell us about your learning preferences
                            </h2>
                          </div>
                          <LearningPreferencesForm
                            preferences={preferences}
                            onUpdate={(field, value) => setPreferences(prev => ({ ...prev, [field]: value }))}
                            onSubmit={handleGenerateRoadmap}
                            isLoading={roadmapData.loading}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row h-screen">
              <div className={`${
                isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
              } transform md:translate-x-0 transition-transform duration-300 fixed md:sticky md:top-0 z-30 md:z-0 w-full md:w-80 h-[calc(100vh-4rem)] md:h-screen bg-white md:flex md:flex-col`}>
                <div className="bg-white/90 backdrop-blur-sm border-r border-gray-200">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={resetToHome}
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back</span>
                      </button>
                      <div>
                        <button
                          onClick={() => setShowRoadmap(!showRoadmap)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors duration-200 text-sm"
                        >
                          <Map className="w-4 h-4" />
                          {showRoadmap ? 'Hide Roadmap' : 'Show Roadmap'}
                        </button>
                      </div>
                    </div>
                    {isSharedView && (
                      <div className="mt-4">
                        <button
                          onClick={handleCloneRoadmap}
                          className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors duration-200 text-sm"
                        >
                          <Copy className="w-4 h-4" />
                          Clone Roadmap
                        </button>
                      </div>
                    )}
                    {showShareTooltip && (
                      <div className="fixed top-4 right-4 px-3 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-50">
                        Link copied!
                      </div>
                    )}
                    <h2 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500 mb-2">
                      {subject}
                    </h2>
                    <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
                      <BookMarked className="w-4 h-4" />
                      <span>{roadmapData.modules.length} modules</span>
                      <span className="mx-1">·</span>
                      <span>{getTotalLessons()} lessons</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        Learning path for <span className="font-medium text-gray-800">{preferences.level}</span>
                      </p>
                      <div className="text-sm font-medium text-blue-600">
                        {calculateCompletionRate()}% complete
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto bg-white/80 backdrop-blur-sm border-r border-gray-200">
                  <div className="p-4 space-y-2">
                    {roadmapData.modules.map((module, index) => (
                      <ModuleCard
                        key={index}
                        title={module.title}
                        lessons={module.lessons}
                        moduleIndex={index}
                        onSelectLesson={(lesson, title) => {
                          generateLessonContent(lesson, title);
                          setIsSidebarOpen(false);
                        }}
                        completedLessons={completedLessons}
                        onToggleComplete={toggleLessonComplete}
                        selectedLesson={selectedLesson}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto bg-white h-[calc(100vh-4rem)] md:h-screen">
                <AnimatePresence mode="wait">
                  {showRoadmap ? (
                    <motion.div
                      key="roadmap"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="p-4 md:p-8 h-full"
                    >
                      <RoadmapFlow modules={roadmapData.modules} />
                    </motion.div>
                  ) : selectedLesson ? (
                    <motion.div
                      key="lesson"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="max-w-4xl mx-auto p-4 md:p-8"
                    >
                      <div className="mb-6 md:mb-8">
                        <div className="flex items-center justify-between gap-2 text-gray-500 mb-4">
                          <span className="text-sm">Lesson {selectedLesson.moduleTitle.split(':')[0]}</span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handleStartQuiz}
                              className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors duration-200"
                            >
                              <GraduationCap className="w-4 h-4" />
                              <span>Take Quiz</span>
                            </button>
                          </div>
                        </div>
                        <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">{selectedLesson.title}</h1>
                        
                        <p className="text-lg md:text-xl text-gray-600">From Module: {selectedLesson.moduleTitle}</p>
                      </div>
                      <div className="prose prose-sm md:prose-lg prose-blue max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-code:text-blue-600 prose-pre:bg-gray-50 prose-pre:border prose-pre:border-gray-200 prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:bg-blue-50/50 prose-blockquote:px-4 prose-blockquote:py-2 prose-blockquote:italic">
                        {selectedLesson.loading ? (
                          <div className="flex items-center gap-2 text-gray-500 py-4">
                            <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                            <span className="text-sm">Generating content...</span>
                          </div>
                        ) : selectedLesson.content ? (
                          <ReactMarkdown>{selectedLesson.content}</ReactMarkdown>
                        ) : (
                          <div className="flex items-center gap-2 text-gray-500 py-4">
                            <span className="text-sm">Click on a lesson to start learning</span>
                          </div>
                        )}
                      </div>
                      {selectedLesson.resourceLinks && (
                        <ResourceLinks links={selectedLesson.resourceLinks} />
                      )}
                      <QuizModal
                        isOpen={showQuiz}
                        onClose={() => setShowQuiz(false)}
                        questions={quizQuestions}
                        loading={loadingQuiz}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="empty"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="h-full flex items-center justify-center text-gray-500 p-4 text-center"
                    >
                      <p>Select a lesson from the sidebar to start learning</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </div>

      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
}

export default App;
